import { computed, ref } from 'vue'
import { setLoadingView } from '@/features/miiaFw/utils/miiaFwUtils'
import { useDisasterStore } from '@/stores/disasterStore'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { GlobalError } from '@/utils/globalError/globalError'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { useAnnotationImageStore, type AnnotationImageUrl } from '@/stores/annotationImageStore'
import { AI_SUGGESTED_LABEL, USER_LABEL } from '@/constants/common/commonConstants'
import type { Annotation } from '@/types/api/runClassificationAnnotation/runClassificationAnnotationTypes'

/** モーダルの開閉フラグ（true: 開, false: 閉） */
const isOpen = ref<boolean>(false)
/** AI提示ラベルが全壊の画像リスト */
const totalCollapseImages = ref<AnnotationImageUrl[]>([])
/** AI提示ラベルが非全壊の画像リスト */
const notTotallyDestroyedImages = ref<AnnotationImageUrl[]>([])
/** 代表ラウンド数 */
const roundCount = ref<number>(-1)
/** 選択中の画像URL（idで管理） */
const selectedImages = ref<Map<string, AnnotationImageUrl>>(new Map())

export const useAerialImageAnnotationModal = () => {
  const disasterStore = useDisasterStore()
  const commonModalStore = useCommonModalStore()
  const annotationImageStore = useAnnotationImageStore()

  const hasTargetImage = computed(
    () => totalCollapseImages.value.length > 0 || notTotallyDestroyedImages.value.length > 0
  )

  /**
   * モーダルを開く
   * @param isButton 「AI倒壊判定の再判定」ボタンでのオープン要求か否か
   * @note アノテーション対象の画像がない場合はダイアログ表示のみ
   */
  const open = async (isButton: boolean): Promise<void> => {
    // データ取得
    await fetchImage()
    // ○ログイン時
    //   画像あり且つ初回の場合、モーダル表示
    // ○AI倒壊判定の再判定ボタン押下時
    //   画像ありの場合、モーダル表示
    //   画像なしの場合、画像なしメッセージ表示
    if (hasTargetImage.value) {
      if (isButton || roundCount.value === 1) {
        isOpen.value = true
      }
    } else if (isButton) {
      commonModalStore.openAlertModal({
        title: '再判定する対象が存在しません',
        body: `再判定をする対象が存在しません。<br>
          AI倒壊判定は毎日15時の時点で取り込まれた画像に対して初回判定が実施されます。再判定は初回判定完了後に行うことができます。<br>
          なお、再判定は次回の15時に実施される処理まで行うことができます。
        `,
        okTitle: '閉じる'
      })
    }
  }

  /**
   * モーダルを閉じる
   * @param なし
   * @returns なし
   */
  const close = (): void => {
    // 各変数リセット
    reset()
    // モーダルを閉じる
    isOpen.value = false
  }

  /**
   * S3からアノテーション対象画像を取得し、画像リストに格納
   * @param なし
   * @returns なし
   */
  const fetchImage = async (): Promise<void> => {
    try {
      setLoadingView(true)
      const govCode = disasterStore.govCode
      const disasterId = disasterStore.disasterId
      if (!govCode || !disasterId) throw new GlobalError([ERROR_CODES.IMAGE_DOWNLOAD_FAILED])

      // アノテーション画像取得API実行
      await annotationImageStore.fetch({
        govCode: disasterStore.disaster!.govCode,
        disasterId: disasterStore.disaster!.disasterId
      })

      // 各プロパティに値を設定
      annotationImageStore.images.map(image => {
        if (image.annotationImage.aiSuggestedLabel === AI_SUGGESTED_LABEL.COLLAPSED) {
          // 全壊の画像
          totalCollapseImages.value.push(image)
        } else {
          // 非全壊の画像
          notTotallyDestroyedImages.value.push(image)
        }
      })
      roundCount.value = annotationImageStore.images.find(e => e.annotationImage.roundCount === 1)
        ? 1
        : -1
    } finally {
      setLoadingView(false)
    }
  }

  /**
   * 画像データの選択／解除
   * @param AnnotationImageUrl
   */
  const toggleSelect = (image: AnnotationImageUrl): void => {
    if (selectedImages.value.has(image.id)) {
      selectedImages.value.delete(image.id)
    } else {
      selectedImages.value.set(image.id, image)
    }
  }

  /**
   * 分類アルゴリズム（アノテーション）起動API用パラメータを生成
   * @returns annotations
   */
  const getAnnotations = (): Annotation[] => {
    // 分類アルゴリズム（アノテーション）起動API用パラメータを生成
    return annotationImageStore.images.map(image => {
      // 選択中の場合、UserLabelにAI判定ラベルとは逆の値を設定
      // 未選択の場合、AI判定ラベルをそのまま設定
      const userLabel = selectedImages.value.has(image.id)
        ? image.annotationImage.aiSuggestedLabel === AI_SUGGESTED_LABEL.COLLAPSED
          ? USER_LABEL.NOT_COLLAPSED
          : USER_LABEL.COLLAPSED
        : image.annotationImage.aiSuggestedLabel
      return {
        annotationId: image.annotationImage.annotationId,
        datasetId: image.annotationImage.datasetId,
        aiSuggestedLabel: image.annotationImage.aiSuggestedLabel,
        userLabel: userLabel,
        roundId: image.annotationImage.roundId,
        roundCount: image.annotationImage.roundCount,
        algorithmType: image.annotationImage.algorithmType
      }
    })
  }

  /**
   * 変数群のリセット
   * @param なし
   * @returns なし
   */
  const reset = (): void => {
    totalCollapseImages.value.length = 0
    notTotallyDestroyedImages.value.length = 0
    selectedImages.value.clear()
    annotationImageStore.reset()
  }

  return {
    isOpen,
    totalCollapseImages,
    notTotallyDestroyedImages,
    hasTargetImage,
    selectedImages,
    open,
    close,
    fetchImage,
    toggleSelect,
    getAnnotations,
    reset
  }
}
